import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import {
  buildYoutubeEmbedUrl,
  extractYoutubeVideoId,
} from '../../types/formFieldSettings';

interface FormSelectVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (youtubeUrl: string) => void;
  initialUrl?: string;
}

export const FormSelectVideoModal: React.FC<FormSelectVideoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialUrl = '',
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen)
    {
      setUrl(initialUrl);
      setError(null);
    }
  }, [isOpen, initialUrl]);

  const videoId = useMemo(() => extractYoutubeVideoId(url), [url]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleConfirm = () => {
    const id = extractYoutubeVideoId(url);
    if (!id)
    {
      setError('Cole uma URL válida do YouTube.');
      return;
    }

    onConfirm(url.trim());
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} title="Selecionar vídeo" onClose={handleClose}>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            URL do YouTube
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) =>
            {
              setUrl(e.target.value);
              setError(null);
            }}
            placeholder="Cole o URL do YouTube"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {videoId ? (
          <div className="overflow-hidden rounded-md border border-gray-200 bg-black">
            <iframe
              title="Pré-visualização do vídeo"
              src={buildYoutubeEmbedUrl(videoId)}
              className="aspect-video w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="mb-3 h-12 w-12 text-red-500"
              fill="currentColor"
              aria-hidden
            >
              <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.5 31.5 0 000 12a31.5 31.5 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.5 31.5 0 0024 12a31.5 31.5 0 00-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z" />
            </svg>
            <p className="text-sm text-gray-500">Cole o URL do YouTube</p>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!videoId}>
            Inserir
          </Button>
        </div>
      </div>
    </Modal>
  );
};
